import { Suspense } from 'react';

import { Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { AppLayout } from '@/components/layouts/AppLayout';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { AuthenticatedOnlyRoute } from '@/components/routes/AuthenticatedOnlyRoute';
import { PublicRoute } from '@/components/routes/PublicRoute';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// Auth routes (GW-221: route-based code splitting — lazy pages live with
// their routes; extracted from App.tsx in the by-domain route split)
// ============================================================================

// Auth Pages
const LoginPage = lazyWithRetry(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const SignupPage = lazyWithRetry(() =>
  import('@/pages/auth/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const ForgotPasswordPage = lazyWithRetry(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazyWithRetry(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const VerifyEmailPage = lazyWithRetry(() =>
  import('@/pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
);
const AcceptInvitationPage = lazyWithRetry(() =>
  import('@/pages/auth/AcceptInvitationPage').then((m) => ({ default: m.AcceptInvitationPage }))
);
const OrganizationSelectionPage = lazyWithRetry(() =>
  import('@/pages/auth/OrganizationSelectionPage').then((m) => ({
    default: m.OrganizationSelectionPage,
  }))
);
const LogoutPage = lazyWithRetry(() =>
  import('@/pages/auth/LogoutPage').then((m) => ({ default: m.LogoutPage }))
);
const TOSReacceptancePage = lazyWithRetry(() =>
  import('@/pages/auth/TOSReacceptancePage').then((m) => ({ default: m.TOSReacceptancePage }))
);

/**
 * The login/signup/forgot-password trio. Mounted INSIDE the shared
 * globe-hosting layout route (the unified AppLayout shell with
 * `showGlobeBackground`, see marketplace.routes.tsx) so these pages share the
 * single persistent globe video node with the landing/browse pages
 * (GW-9376/GW-10438). PublicRoute (redirect-if-authenticated) wraps ONLY these
 * children, exactly as before the split.
 *
 * GW-10515: these pages render through the AppLayout `centered` variant
 * (generic centering + globe z-10 lift) with the nested AuthLayout supplying
 * the `max-w-md` card width. The `centered` variant replaced the auth-specific
 * `auth-card` variant (GW-10437), moving the width concern into AuthLayout so
 * the globe/z-index stays a pure layout concern. The rendered markup is
 * unchanged from what the `auth-card` variant produced.
 */
export const globeAuthRoutes = (
  <>
    <Route element={<PublicRoute />}>
      <Route element={<AppLayout variant="centered" />}>
        <Route element={<AuthLayout />}>
          <Route
            path="/auth/login"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="/auth/signup"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <SignupPage />
              </Suspense>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <ForgotPasswordPage />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Route>
  </>
);

/**
 * Remaining /auth/* and invitation routes. Mounted directly under
 * RouteErrorBoundary in App.tsx (no shared layout).
 */
export const authRoutes = (
  <>
    {/* GW-6092/GW-9376: the /marketplace/search-next backwards-compat
        alias and the login/signup/forgot-password auth children moved
        up into the shared globe-hosting layout route (near the `/` landing)
        so they share the single persistent globe video node.
        reset-password/verify-email stay here under PublicRoute (they
        render @gw/ui's own full-screen AuthShell — see below). */}

    {/* Auth routes (redirect if authenticated) */}
    <Route element={<PublicRoute />}>
      {/* GW-8062: VerifyEmailPage/ResetPasswordPage now render @gw/ui's
        AuthShell-wrapped pages (GW-8047), which are already full-screen,
        self-contained shells (background + centering + optional logo/
        footer slots) — the same reason OrganizationSelectionPage below
        is NOT nested inside the AppLayout `centered` variant. Nesting
        AuthShell inside the centered variant's own container would double
        both layouts. The PublicRoute guard above is unchanged. */}
      <Route
        path="/auth/reset-password"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/auth/verify-email"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <VerifyEmailPage />
          </Suspense>
        }
      />
    </Route>

    {/* Organization selection (after login, before main app) */}
    {/* GW-442: Wrap in AuthenticatedOnlyRoute to prevent unauthenticated access */}
    <Route element={<AuthenticatedOnlyRoute />}>
      <Route
        path="/auth/select-organization"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <OrganizationSelectionPage />
          </Suspense>
        }
      />
    </Route>

    {/* Logout page — GW-10440: renders chromeless through AppLayout's bare variant */}
    <Route element={<AppLayout variant="bare" />}>
      <Route
        path="/auth/logout"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <LogoutPage />
          </Suspense>
        }
      />
    </Route>

    {/* Invitation acceptance (accessible to both authenticated and unauthenticated users) */}
    <Route
      path="/invitations/:code/accept"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <AcceptInvitationPage />
        </Suspense>
      }
    />

    {/* TOS Re-acceptance page - requires auth but NOT org selection (GW-450) */}
    <Route element={<AuthenticatedOnlyRoute />}>
      <Route
        path="/auth/accept-tos"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <TOSReacceptancePage />
          </Suspense>
        }
      />
    </Route>
  </>
);
